import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Search, Sparkles } from "lucide-react";
import { Container } from "@/site/ui";

const LOGO = "https://customer-assets-0z36b82j.emergentagent.net/job_stock-alert-pro-21/artifacts/use5eg02_Raphadon%20Logo%20%28without%20background%29.png";

const UTILITY = [
  { label: "Get A Quote", to: "/get-a-quote" },
  { label: "Track Order", to: "/track-order" },
  { label: "Photos", to: "/photos" },
  { label: "Videos", to: "/videos" },
  { label: "Testimonials", to: "/testimonials" },
  { label: "Downloads", to: "/downloads" },
];

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Artificial Limbs / Prosthetic Services" },
  { to: "/services", label: "Braces & Splints / Orthotic Services" },
  { to: "/services", label: "Silicone Prosthesis / Cosmetic Restoration" },
  { to: "/services", label: "Diabetic Foot Care / Foot Care Services" },
  { to: "/medical-tourism", label: "Medical Tourism" },
  { to: "/contact", label: "Contact Us" },
];

export default function Navbar({ content }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const navCfg = content?.nav || {};
  const mainNav = navCfg.main?.length ? navCfg.main : NAV;
  const utility = navCfg.utility?.length ? navCfg.utility : UTILITY;
  const bionicLabel = navCfg.bionic_label || "AI Bionic";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const search = (e) => { e.preventDefault(); if (q.trim()) navigate("/services"); };

  return (
    <header data-testid="site-navbar" className="fixed top-0 inset-x-0 z-50">
      {/* Red utility bar */}
      <div className={`bg-[var(--rgh-primary)] text-white transition-[height,opacity] duration-300 overflow-hidden ${scrolled ? "h-0 opacity-0" : "h-auto opacity-100"}`} data-testid="top-utility-bar">
        <Container className="flex items-center justify-between gap-4 py-2">
          <nav className="hidden md:flex items-center gap-1 text-[13px] font-medium">
            {utility.map((u, i) => (
              <span key={i} className="flex items-center">
                {i > 0 && <span className="mx-2 text-white/40">|</span>}
                <Link to={u.to} data-testid={`utility-${u.label.replace(/\s+/g, "-").toLowerCase()}`} className="hover:text-white/80 transition-colors duration-200 whitespace-nowrap">{u.label}</Link>
              </span>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3">
            <form onSubmit={search} className="flex items-center bg-white rounded-full pl-3.5 pr-1 py-1 w-full max-w-[240px]">
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="site-search-input" placeholder="Enter Keyword to Search" className="flex-1 min-w-0 bg-transparent text-[13px] text-[#0A0A0A] placeholder:text-[#A3A3A3] outline-none" />
              <button type="submit" data-testid="site-search-btn" className="h-7 w-7 shrink-0 inline-flex items-center justify-center rounded-full text-[var(--rgh-primary)] hover:bg-[rgb(var(--rgh-primary-rgb)/0.10)]"><Search size={15} /></button>
            </form>
            <Link to="/login" data-testid="navbar-login" className="hidden sm:inline-flex shrink-0 items-center rounded-full border border-white/50 px-4 py-1.5 text-[13px] font-semibold text-white transition-colors duration-200 hover:bg-white hover:text-[var(--rgh-primary)]">Login</Link>
          </div>
        </Container>
      </div>

      {/* Main nav row */}
      <div className={`transition-[background-color,box-shadow] duration-300 ${scrolled ? "bg-white/80 backdrop-blur-xl shadow-[0_1px_20px_rgba(0,0,0,0.06)]" : "bg-white"} border-b border-black/5`}>
        <Container className="flex h-[140px] items-center justify-between gap-4">
          <Link to="/" data-testid="navbar-logo" className="flex items-center shrink-0">
            <img src={LOGO} alt="Raphadon — helps to live better" className="h-24 md:h-[124px] w-auto object-contain" />
          </Link>

          <nav className="hidden xl:flex items-center gap-4">
            {mainNav.map((n, i) => (
              <NavLink key={i} to={n.to} end={n.end || n.to === "/"} data-testid={`nav-${n.label.replace(/[^a-z]+/gi, "-").toLowerCase().replace(/^-|-$/g, "")}`}
                className={({ isActive }) =>
                  `text-[13px] font-medium leading-tight max-w-[120px] transition-colors duration-200 ${isActive ? "text-[var(--rgh-primary)]" : "text-[#0A0A0A] hover:text-[var(--rgh-primary)]"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            <NavLink to="/ai-bionic" data-testid="nav-ai-bionic"
              className="hidden xl:inline-flex group relative items-center justify-center gap-2 overflow-hidden rounded-full px-5 py-2.5 text-[13px] font-semibold leading-none tracking-wide text-white whitespace-nowrap ring-1 ring-white/25 shadow-[0_6px_18px_rgb(var(--rgh-primary-rgb)/0.45)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_26px_rgb(var(--rgh-primary-rgb)/0.6)]"
              style={{ backgroundImage: "linear-gradient(110deg, var(--rgh-primary) 0%, #7A0C28 45%, var(--rgh-primary) 90%)", backgroundSize: "200% 100%", animation: "rgh-shimmer 3s linear infinite" }}>
              <Sparkles size={14} className="animate-pulse shrink-0" />
              <span>{bionicLabel}</span>
            </NavLink>
            <button data-testid="navbar-menu-toggle" onClick={() => setOpen((o) => !o)} className="xl:hidden inline-flex h-10 w-10 items-center justify-center rounded-full hover:bg-black/5" aria-label="Menu">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </Container>
      </div>

      {open && (
        <div className="xl:hidden bg-white border-t border-black/5 max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
          <Container className="py-4 flex flex-col gap-1">
            <NavLink to="/ai-bionic" onClick={() => setOpen(false)} data-testid="mobile-nav-ai-bionic"
              className="mb-1 inline-flex items-center gap-2 rounded-full px-4 py-3 text-[15px] font-semibold text-white shadow-md"
              style={{ backgroundImage: "linear-gradient(110deg, var(--rgh-primary) 0%, #7A0C28 45%, var(--rgh-primary) 90%)", backgroundSize: "200% 100%", animation: "rgh-shimmer 3s linear infinite" }}>
              <Sparkles size={16} className="animate-pulse" /> {bionicLabel}
            </NavLink>
            {mainNav.map((n, i) => (
              <NavLink key={i} to={n.to} end={n.end || n.to === "/"} onClick={() => setOpen(false)}
                className={({ isActive }) => `py-2.5 text-[15px] font-medium ${isActive ? "text-[var(--rgh-primary)]" : "text-[#0A0A0A]"}`}>
                {n.label}
              </NavLink>
            ))}
            <div className="mt-2 pt-3 border-t border-black/10 flex flex-wrap gap-x-4 gap-y-2 text-sm text-[#525252]">
              {utility.map((u, i) => <Link key={i} to={u.to} onClick={() => setOpen(false)} className="hover:text-[var(--rgh-primary)]">{u.label}</Link>)}
            </div>
            <Link to="/login" onClick={() => setOpen(false)} className="mt-3 inline-flex justify-center rounded-full bg-[var(--rgh-primary)] px-5 py-3 text-sm font-semibold text-white">Login</Link>
          </Container>
        </div>
      )}
    </header>
  );
}
